import { supabase } from '../services/supabase';
import { getStateName, getCountry } from './geography';
import { getLocalDateString } from './today';

// Sentinel tourName for "every show, regardless of tour" — also doubles as
// its own dropdown label. A real tour literally named this isn't a realistic
// collision risk for this catalog.
export const ALL_SHOWS = 'All Shows';

// `setlist_songs.song_id` is a UUID (~37 chars with the comma), not a short
// int — an `.in()` filter listing every song in the archive builds a URL
// well past Supabase's gateway limit and comes back as a silent 400, which
// the caller's `if (error) break` swallows as "no rows". Used below for the
// debuts-scoped scan (`.in('song_id', chunk)`); the main setlist_songs fetch
// used to have the same problem via `.in('show_id', playedIds)` under the
// "All Shows" option, but that's now a join-filtered query (see
// fetchTourSetlistRows) with no id list in the URL at all.
const IN_CHUNK_SIZE = 100;

async function fetchAllInChunks(buildQuery, ids) {
    let rows = [];
    for (let i = 0; i < ids.length; i += IN_CHUNK_SIZE) {
        const chunk = ids.slice(i, i + IN_CHUNK_SIZE);
        for (let rangeStart = 0; ;) {
            const { data: page, error } = await buildQuery(chunk).range(rangeStart, rangeStart + 999);
            if (error || !page?.length) break;
            rows = rows.concat(page);
            if (page.length < 1000) break;
            rangeStart += 1000;
        }
    }
    return rows;
}

// Every already-played setlist_songs row for a tour (or, with ALL_SHOWS, every
// already-played row regardless of tour), filtered via a join on shows instead
// of an `.in('show_id', ids)` list — this only needs tourName/todayStr, so a
// caller can fire it off at the same time as its own "which shows are in this
// tour" query instead of waiting for that query's ids first. The two used to be
// forced sequential for exactly that reason; PageSpeed's network-dependency-tree
// audit showed that chain (shows query -> setlist query -> debuts-batch) as the
// single longest critical path on the homepage. Swallows errors as an empty
// page, same as fetchAllInChunks above, so a caller that `await`s this without
// handling rejection (because it started the promise before it had anything
// else to check) never sees an unhandled rejection.
async function fetchTourSetlistRows(tourName, todayStr, needsExtras) {
    let rows = [];
    for (let rangeStart = 0; ;) {
        let query = supabase
            .from('setlist_songs')
            .select(`
                song_id, show_id, set_number, is_encore, song_order, notes, performance_type, jams_into,
                songs!setlist_songs_song_id_fkey (
                    title, is_original, live_debut_suppressed${needsExtras ? ',\n                    album_songs ( albums ( title, release_date ) )' : ''}
                ),
                shows!inner ( show_date )
            `)
            .lte('shows.show_date', todayStr)
            .not('performance_type', 'in', '(dj,soundcheck)')
            .order('id')
            .range(rangeStart, rangeStart + 999);
        if (tourName !== ALL_SHOWS) query = query.eq('shows.tour_name', tourName);
        const { data: page, error } = await query;
        if (error || !page?.length) break;
        rows = rows.concat(page);
        if (page.length < 1000) break;
        rangeStart += 1000;
    }
    return rows;
}

// Every song actually on an album, by title — independent of any tour. Used
// to find which of an album's tracks got zero plays on a given tour (the
// Rarest Songs by Album stat's "never played" note): tourSongCounts only
// ever knows about songs that appear in a tour's setlists, so a track that
// was never played at all simply never shows up there at all; this is the
// only way to know it exists and should be called out as absent.
export async function fetchAlbumTrackTitles(albumTitle) {
    const { data: album } = await supabase.from('albums').select('id').eq('title', albumTitle).maybeSingle();
    if (!album) return [];
    const { data: rows } = await supabase
        .from('album_songs')
        .select('track_order, songs(title)')
        .eq('album_id', album.id)
        .order('track_order', { ascending: true, nullsFirst: false });
    return (rows || []).map(r => r.songs?.title).filter(Boolean);
}

// The tour of the most recent show that's actually happened — same "current tour"
// definition used by the homepage widget, so the dedicated tour stats page defaults
// to the same tour it links out from.
export async function fetchDefaultTourName() {
    const todayStr = getLocalDateString();
    const { data } = await supabase
        .from('shows')
        .select('tour_name')
        .lte('show_date', todayStr)
        .not('tour_name', 'is', null)
        // A non-tour side event (an afterparty, a one-off) stores tour_name
        // as '' rather than null, so the null check above alone doesn't
        // exclude it — one dated later the same night (higher day_order)
        // than the actual tour show would otherwise win this sort.
        .neq('tour_name', '')
        .order('show_date', { ascending: false })
        .order('day_order', { ascending: false })
        .order('id')
        .limit(1);
    return data?.[0]?.tour_name || null;
}

// Every distinct tour_name with at least `minShows` scheduled shows, with its date
// range, newest-last-show-first. Default (5) filters out one-off/benefit shows
// entered with their own "tour" name, for the public tour picker; admin screens
// that need to see every tour regardless of size pass minShows: 0.
// Paginated with a stable order since the shows table can exceed PostgREST's
// 1000-row default.
const MIN_TOUR_SHOWS = 5;

export async function fetchTourList({ minShows = MIN_TOUR_SHOWS } = {}) {
    let rows = [];
    for (let rangeStart = 0; ;) {
        const { data: page, error } = await supabase
            .from('shows')
            .select('tour_name, show_date')
            .not('tour_name', 'is', null)
            .order('id')
            .range(rangeStart, rangeStart + 999);
        if (error || !page?.length) break;
        rows = rows.concat(page);
        if (page.length < 1000) break;
        rangeStart += 1000;
    }

    const byTour = {};
    rows.forEach(({ tour_name, show_date }) => {
        if (!tour_name) return;
        const entry = (byTour[tour_name] ||= { tourName: tour_name, firstDate: show_date, lastDate: show_date, showCount: 0 });
        entry.showCount++;
        if (show_date < entry.firstDate) entry.firstDate = show_date;
        if (show_date > entry.lastDate) entry.lastDate = show_date;
    });

    return Object.values(byTour)
        .filter(t => t.showCount >= minShows)
        .sort((a, b) => b.lastDate.localeCompare(a.lastDate));
}

// Distinct-show play counts per song title for every already-played show (show_date
// <= today) on a tour, sorted most- to least-played. Paginated with a stable order
// so repeated fetches return consistent results (see [[pagination-determinism]]).
// Excludes performance_type='dj'/'soundcheck' rows, same treatment as the server's
// equivalent tour-rarity computation (GET /api/shows/:id/tour-rarity) — neither is
// an actual live performance, so neither should count here either.
// `needsExtras` (default true, unchanged behavior for every existing caller
// — the full Tour Stats pages/IG generator all need the complete picture)
// gates the two most expensive parts of this function: the album_songs join
// on every row, and a *second* full chunked setlist_songs scan (scoped by
// song_id) just to compute live debuts. TourStatsWidget — the homepage
// widget that runs this on every single page load — needs neither: it gets
// debut counts from the cheap, server-cached GET /shows/debuts-batch
// instead, and doesn't show an album breakdown at all. Passing
// needsExtras: false there cuts its Supabase round-trips roughly in half
// (confirmed via PageSpeed's network-dependency-tree report, which showed
// this homepage load firing six separate setlist_songs requests).
export async function fetchTourSongCounts(tourName, { stateFilter, countryFilter, needsExtras = true, onPlayedIds } = {}) {
    const todayStr = getLocalDateString();

    // Fired immediately, in parallel with the shows query below — it only
    // depends on tourName/todayStr, not on that query's results. Verified
    // against real data to return byte-identical rows to the old
    // `.in('show_id', playedIds)` approach it replaces.
    const setlistRowsPromise = fetchTourSetlistRows(tourName, todayStr, needsExtras);

    // venues included for the Tour Map (ShowMapShare, same shape it already
    // reads off a user's attended/upcoming shows); artist_name only needed
    // for linking a Live Debut entry to its actual show page.
    const selectCols = 'id, show_date, artist_name, is_cancelled, venues ( id, name, city, state_country, latitude, longitude )';
    let tourShows;
    if (tourName === ALL_SHOWS) {
        // No tour_name filter at all — includes one-off/benefit shows that
        // have no tour_name too. Paginated since this can exceed PostgREST's
        // 1000-row default, unlike a single tour's show list.
        let rows = [];
        for (let rangeStart = 0; ;) {
            const { data: page, error } = await supabase
                .from('shows')
                .select(selectCols)
                .order('show_date')
                .order('day_order', { ascending: true })
                .order('id')
                .range(rangeStart, rangeStart + 999);
            if (error || !page?.length) break;
            rows = rows.concat(page);
            if (page.length < 1000) break;
            rangeStart += 1000;
        }
        tourShows = rows;
    } else {
        const { data, error: tourError } = await supabase
            .from('shows')
            .select(selectCols)
            .eq('tour_name', tourName)
            .order('show_date')
            .order('day_order', { ascending: true })
            .order('id');
        if (tourError) return null;
        tourShows = data;
    }

    if (!tourShows?.length) return null;

    if (stateFilter) tourShows = tourShows.filter(s => getStateName(s.venues?.state_country) === stateFilter);
    if (countryFilter) tourShows = tourShows.filter(s => getCountry(s.venues?.state_country) === countryFilter);
    if (!tourShows.length) return null;

    // allTourShows (including canceled) backs pastShowsWithCanceled/
    // upcomingShowsWithCanceled below — for a caller that still wants to list
    // every scheduled show (e.g. TourPage's own per-show list), tagged. A
    // canceled show never happened, though, so it's excluded from `tourShows`
    // from here on — every count/stat this function produces (totalShows,
    // played/upcoming, song counts, map pins) — same as it's excluded from
    // "shows attended" everywhere else.
    const allTourShows = tourShows;
    tourShows = tourShows.filter(s => !s.is_cancelled);

    const allPast = allTourShows.filter(s => s.show_date <= todayStr);
    const allUpcoming = allTourShows.filter(s => s.show_date > todayStr);
    const played = tourShows.filter(s => s.show_date <= todayStr);
    const upcoming = tourShows.filter(s => s.show_date > todayStr);
    const playedIds = played.map(s => s.id);

    // Fired as soon as playedIds is known, before the setlist_songs fetch below —
    // lets a caller (TourStatsWidget) start its own independent debuts-batch
    // request in parallel with that fetch instead of waiting for this whole
    // function (and everything it computes from setlist_songs) to resolve first.
    onPlayedIds?.(playedIds);

    if (playedIds.length === 0) {
        return { totalShows: tourShows.length, playedShows: 0, showsWithSetlist: 0, firstDate: allTourShows[0].show_date, lastDate: allTourShows[allTourShows.length - 1].show_date, playedIds: [], songCounts: [], openingSongs: [], closingSongs: [], liveDebuts: [], biggestSetlist: null, albumBreakdown: [], songsByAlbum: {}, otherOriginals: 0, covers: 0, makeAmericaEnergyLines: [], setlistSongsByShow: {}, showsWithSetlistInfo: [], pastShows: played, upcomingShows: upcoming, pastShowsWithCanceled: allPast, upcomingShowsWithCanceled: allUpcoming };
    }

    // Intersected with playedIds rather than trusted as-is: setlistRowsPromise
    // is already filtered by tourName + show_date, but stateFilter/countryFilter
    // (applied to tourShows above, after setlistRowsPromise had already started)
    // can narrow playedIds further than that promise knows about.
    const playedIdSet = new Set(playedIds);
    const rows = (await setlistRowsPromise).filter(r => playedIdSet.has(r.show_id));

    // Distinct shows per song — a song sandwiched (jammed out of and back into)
    // within one show still only counts once for that show.
    const showSetsByTitle = {};
    const isOriginalByTitle = {};
    // Distinct songs per show (for the average), and one representative row per
    // song_id (for the tour-wide album breakdown) — both keyed off song_id so a
    // sandwiched song still only counts once per show / once tour-wide.
    const songIdsByShow = {};
    const songMetaById = {};
    // Distinct shows per song_id — same idea as showSetsByTitle above, but
    // keyed by id (not title) so the album breakdown's per-song play count
    // can't be thrown off by two different songs sharing a title.
    const showSetsBySongId = {};
    rows.forEach(r => {
        // A tease doesn't count as the song actually being played — same rule
        // the show page, Instagram graphic and homepage list previews already
        // apply to a single performance's own Live/Tour Debut and Rare tags.
        // Here it means a song only ever teased this tour doesn't show up in
        // Most Played/Rarest/the album breakdown at all, and isn't credited
        // toward a show's songs-played count (avg songs/show, Biggest Setlist).
        if (r.performance_type === 'tease') return;
        const title = r.songs?.title;
        if (title) {
            (showSetsByTitle[title] ||= new Set()).add(r.show_id);
            if (r.songs?.is_original === false) isOriginalByTitle[title] = false;
            else if (!(title in isOriginalByTitle)) isOriginalByTitle[title] = r.songs?.is_original ?? true;
        }

        if (r.song_id) {
            (songIdsByShow[r.show_id] ||= new Set()).add(r.song_id);
            (showSetsBySongId[r.song_id] ||= new Set()).add(r.show_id);
            if (!songMetaById[r.song_id]) {
                songMetaById[r.song_id] = { title, isOriginal: r.songs?.is_original, albumSongs: r.songs?.album_songs || [], liveDebutSuppressed: r.songs?.live_debut_suppressed };
            }
        }
    });

    // Every show's own ordered setlist (encores last, then set number, then
    // position within the set) — unlike showSetsByTitle/songCounts above,
    // teases are kept here (and not collapsed for sandwiching) since this is
    // the actual setlist as performed, not a tally. Used by the Tour Mosaic
    // stat card, which renders each show's real setlist on its own tile.
    const setlistOrderKey = r => (r.is_encore ? 1 : 0) * 1000000 + (r.set_number ?? 0) * 10000 + (r.song_order ?? 0);
    const setlistRowsByShow = {};
    rows.forEach(r => { (setlistRowsByShow[r.show_id] ||= []).push(r); });
    const setlistSongsByShow = {};
    Object.entries(setlistRowsByShow).forEach(([showId, showRows]) => {
        setlistSongsByShow[showId] = [...showRows]
            .sort((a, b) => setlistOrderKey(a) - setlistOrderKey(b))
            .map(r => ({
                song_id: r.song_id,
                title: r.songs?.title,
                performance_type: r.performance_type,
                is_original: r.songs?.is_original,
                jams_into: r.jams_into,
            }))
            .filter(s => s.title);
    });

    // Live debuts: songs whose live debut — the earliest setlist_songs
    // performance date of all time, never songs.created_at — landed on one
    // of this (filtered) tour's played shows. Needs each song's true
    // earliest date across its ENTIRE performance history, not just this
    // tour, so a song that first appeared on an earlier tour never gets
    // mislabeled as debuting here. Same "earliest date" definition and
    // dj/soundcheck exclusion as server/utils/setlistIndex.js's
    // globalEarliestBySong, just computed client-side since this page has
    // no server route of its own to share that cache with.
    const scopedSongIds = needsExtras ? [...new Set(rows.map(r => r.song_id).filter(id => id != null))] : [];
    const debutRows = needsExtras ? await fetchAllInChunks(
        (chunk) => supabase
            .from('setlist_songs')
            .select('song_id, shows ( show_date )')
            .in('song_id', chunk)
            .not('performance_type', 'in', '(dj,soundcheck)')
            .order('id'),
        scopedSongIds
    ) : [];
    const globalEarliestBySong = {};
    debutRows.forEach(r => {
        const date = r.shows?.show_date;
        if (!date || r.song_id == null) return;
        if (!globalEarliestBySong[r.song_id] || date < globalEarliestBySong[r.song_id]) {
            globalEarliestBySong[r.song_id] = date;
        }
    });
    const showById = {};
    played.forEach(s => { showById[s.id] = s; });

    // "Make America Fuk Again" Energy — a running bit on this tour where the
    // per-performance notes name a different "energy" each night (e.g.
    // "Hunter Biden energy"). A one-off novelty stat for the Tour Stats IG
    // generator, not a general "any song's notes" feature — hardcoded to
    // this specific title rather than parameterized.
    const ENERGY_SONG_TITLE = 'Make America Fuk Again';
    const makeAmericaEnergyLines = rows
        .filter(r => r.songs?.title === ENERGY_SONG_TITLE && r.notes?.length)
        // notes is a TEXT[] column (a performance can carry more than one note) —
        // joined into one display string so this stat's existing exact-string
        // grouping (see tourStatTypes.js) keeps working unchanged.
        .map(r => ({ date: showById[r.show_id]?.show_date, venue: showById[r.show_id]?.venues || null, notes: r.notes.join(' / ') }))
        .filter(l => l.date)
        .sort((a, b) => b.date.localeCompare(a.date));

    const liveDebuts = [];
    scopedSongIds.forEach(songId => {
        const meta = songMetaById[songId];
        const earliest = globalEarliestBySong[songId];
        const showIdsHere = showSetsBySongId[songId];
        if (!meta || !earliest || !showIdsHere || meta.liveDebutSuppressed) return;
        const debutShowId = [...showIdsHere].find(showId => showById[showId]?.show_date === earliest);
        if (debutShowId == null) return;
        liveDebuts.push({
            songId, title: meta.title, isOriginal: meta.isOriginal,
            showId: debutShowId, showDate: earliest,
            artistName: showById[debutShowId].artist_name,
            venue: showById[debutShowId].venues || null,
        });
    });
    liveDebuts.sort((a, b) => a.showDate.localeCompare(b.showDate) || a.title.localeCompare(b.title));

    // Biggest setlist: the show with the most distinct songs played — a
    // SONG COUNT, not a time duration (this app has no set-length data
    // anywhere), so callers must caption this as "most songs," never
    // "longest show."
    let biggestSetlist = null;
    Object.entries(songIdsByShow).forEach(([showId, songIds]) => {
        const s = showById[showId];
        if (!s) return;
        const songCount = songIds.size;
        if (!biggestSetlist || songCount > biggestSetlist.songCount) {
            biggestSetlist = { showId, songCount, showDate: s.show_date, artistName: s.artist_name, venue: s.venues || null };
        }
    });

    // Opener/closer: the earliest/latest performed song per show, ordered
    // set1 < set2 < set3 < encore and by song_order within a set — not just
    // set1/song_order 1, since an incomplete or oddly-logged setlist might
    // not start there. One opener and one closer per show, so no sandwich
    // double-counting to worry about here (unlike songCounts above).
    const orderKey = r => (r.is_encore ? 4 : r.set_number) * 10000 + r.song_order;
    const openerByShow = {};
    const closerByShow = {};
    rows.forEach(r => {
        if (r.set_number == null || r.song_order == null) return;
        const key = orderKey(r);
        const opener = openerByShow[r.show_id];
        if (!opener || key < opener.key) openerByShow[r.show_id] = { row: r, key };
        const closer = closerByShow[r.show_id];
        if (!closer || key > closer.key) closerByShow[r.show_id] = { row: r, key };
    });

    function tallyByTitle(byShow) {
        const counts = {};
        Object.values(byShow).forEach(({ row }) => {
            const title = row.songs?.title;
            if (!title) return;
            const entry = (counts[title] ||= { title, count: 0, isOriginal: row.songs?.is_original });
            entry.count++;
        });
        return Object.values(counts).sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
    }

    const openingSongs = tallyByTitle(openerByShow);
    const closingSongs = tallyByTitle(closerByShow);

    const showsWithSetlist = Object.keys(songIdsByShow).length;

    // Every show that actually has a setlist logged (a subset of `played` —
    // most of a tour's played dates usually do, but not always yet), each
    // with its date/venue — the complete "roll call" a caller diffs a
    // song's own showIds against to find which of those shows it WASN'T
    // played at. Powers the "How the Hell Did I Miss This Song?" stat.
    const showsWithSetlistInfo = Object.keys(songIdsByShow)
        .map(id => showById[id])
        .filter(Boolean)
        .map(s => ({ id: s.id, showDate: s.show_date, artistName: s.artist_name, venue: s.venues || null }))
        .sort((a, b) => a.showDate.localeCompare(b.showDate));

    // Same "rare" threshold and denominator as the show page's per-song Rare badge:
    // played at fewer than 15% of tour shows that actually have a setlist logged
    // (not every scheduled show — most of a tour is still-unplayed future dates).
    const songCounts = Object.entries(showSetsByTitle)
        .map(([title, shows]) => {
            const showIds = [...shows];
            return {
                title,
                count: shows.size,
                isOriginal: isOriginalByTitle[title],
                isRare: showsWithSetlist > 0 && shows.size / showsWithSetlist < 0.15,
                // Sorted show dates this title was played on, within this tour's
                // scope — lets a caller (e.g. the Rarest Songs stat) name the
                // actual date for a song played only once, not just the count.
                showDates: showIds.map(id => showById[id]?.show_date).filter(Boolean).sort(),
                // Every show id this title was played at (any count, not just
                // 1) — lets a caller work out which of the tour's shows it was
                // NOT played at (e.g. the "How the Hell Did I Miss This Song?"
                // stat, by diffing against showsWithSetlistInfo below).
                showIds,
                // Only meaningful when count === 1 — the one show it was played
                // at, with venue/artist for linking straight to that show page.
                // Powers the "Rarest of the Rare" (played-once) card.
                onlyShow: shows.size === 1 && showById[showIds[0]]
                    ? {
                        showId: showIds[0],
                        showDate: showById[showIds[0]].show_date,
                        artistName: showById[showIds[0]].artist_name,
                        venue: showById[showIds[0]].venues || null,
                    }
                    : null,
            };
        })
        .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));

    const totalSongInstances = Object.values(songIdsByShow).reduce((sum, set) => sum + set.size, 0);
    const avgSongsPerShow = showsWithSetlist > 0 ? totalSongInstances / showsWithSetlist : 0;

    // Album breakdown across the whole tour: a song with a real album_songs
    // association is attributed to its earliest-released album (same rule as
    // the per-show breakdown on the show page) regardless of is_original — a
    // cover that's an actual album track (several on Mutiny After Midnight,
    // for instance) counts toward that album, not the generic Covers bucket.
    // Summed by total distinct-show play count, not just whether it was
    // played at all, so an album with one song played 15 times outweighs one
    // with three songs played once each. Only a song with no album
    // association at all falls through to otherOriginals/covers.
    const albumCounts = new Map();
    // Per-album song breakdown — same per-song primary-album attribution as
    // albumCounts just below, grouped the other way (by album, listing its
    // songs) instead of summed into one total per album. Used by the Song
    // Count by Album stat, where an admin picks one of these albums and
    // gets every one of its songs ranked by this tour's play count.
    const songsByAlbum = {};
    let otherOriginals = 0;
    let covers = 0;
    // Skipped along with the album_songs join above when !needsExtras — the
    // field is absent from every row in that case, so running this would
    // silently misclassify every song as "no album" instead of just being
    // pointlessly expensive.
    if (needsExtras) {
        Object.entries(songMetaById).forEach(([songId, { title, isOriginal, albumSongs }]) => {
            const playCount = showSetsBySongId[songId]?.size || 0;
            const assocs = (albumSongs || []).filter(as => as.albums);
            if (assocs.length > 0) {
                const primary = [...assocs].sort((a, b) => {
                    const ra = a.albums?.release_date || '';
                    const rb = b.albums?.release_date || '';
                    if (!ra) return 1;
                    if (!rb) return -1;
                    return ra.localeCompare(rb);
                })[0];
                const albumTitle = primary.albums?.title || 'Other';
                albumCounts.set(albumTitle, (albumCounts.get(albumTitle) || 0) + playCount);
                (songsByAlbum[albumTitle] ||= []).push({ title, count: playCount });
            } else if (isOriginal === false) {
                covers += playCount;
            } else {
                otherOriginals += playCount;
            }
        });
    }
    Object.values(songsByAlbum).forEach(list => list.sort((a, b) => b.count - a.count || a.title.localeCompare(b.title)));
    const albumBreakdown = [...albumCounts.entries()]
        .map(([title, count]) => ({ title, count }))
        .sort((a, b) => b.count - a.count);

    return {
        totalShows: tourShows.length,
        playedShows: played.length,
        showsWithSetlist,
        firstDate: tourShows[0].show_date,
        lastDate: tourShows[tourShows.length - 1].show_date,
        playedIds,
        songCounts,
        openingSongs,
        closingSongs,
        liveDebuts,
        biggestSetlist,
        avgSongsPerShow,
        albumBreakdown,
        songsByAlbum,
        otherOriginals,
        covers,
        makeAmericaEnergyLines,
        setlistSongsByShow,
        showsWithSetlistInfo,
        pastShows: played,
        upcomingShows: upcoming,
        // Same shape as pastShows/upcomingShows but includes canceled shows —
        // for a caller that still wants to list every scheduled show (tagged),
        // like TourPage's own per-show list, as opposed to every stat/count
        // above and the Tour Map pins, which stay canceled-free.
        pastShowsWithCanceled: allPast,
        upcomingShowsWithCanceled: allUpcoming,
    };
}
